import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableSortLabel, TextField, InputAdornment, Chip, Tooltip, CircularProgress,
  Button, LinearProgress,
} from '@mui/material';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

// ── Styles ────────────────────────────────────────────────────────────────────
const TH = {
  backgroundColor: '#131318',
  color: '#94a3b8',
  fontWeight: 700,
  fontSize: '0.7rem',
  py: 0.75,
  borderBottom: '2px solid #4a7096',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  position: 'sticky',
  top: 0,
  zIndex: 2,
};

const SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: '#4a7096 #08121f',
  '&::-webkit-scrollbar': { width: 6, height: 6 },
  '&::-webkit-scrollbar-track': { background: '#131318', borderRadius: 11 },
  '&::-webkit-scrollbar-thumb': { background: 'rgba(245,166,35,0.4)', borderRadius: 11 },
};

// Frozen left panel width (rank + team)
const FROZEN_W = 200;
const ROW_H = 44;

// ── Column definitions ────────────────────────────────────────────────────────
const BASIC_COLS = [
  { key: 'wins',       label: 'W',          tip: 'Wins',                           fmt: v => v ?? '—' },
  { key: 'losses',     label: 'L',          tip: 'Losses',                         fmt: v => v ?? '—' },
  { key: 'pf',         label: 'PF',         tip: 'Points For',                     fmt: v => v != null ? Math.round(v) : '—' },
  { key: 'pa',         label: 'PA',         tip: 'Points Against',                 fmt: v => v != null ? Math.round(v) : '—' },
  { key: 'diff',       label: 'Diff',       tip: 'Point Differential',             fmt: (v) => v != null ? (v > 0 ? `+${Math.round(v)}` : Math.round(v)) : '—', diffCol: true },
  { key: 'pfg',        label: 'PF/G',       tip: 'Points For per game',            fmt: v => v != null ? v.toFixed(1) : '—' },
  { key: 'pag',        label: 'PA/G',       tip: 'Points Against per game',        fmt: v => v != null ? v.toFixed(1) : '—' },
  { key: 'passYdsPg',  label: 'Pass Yds/G', tip: 'Passing yards per game',         fmt: v => v != null ? v.toFixed(0) : '—' },
  { key: 'rushYdsPg',  label: 'Rush Yds/G', tip: 'Rushing yards per game',         fmt: v => v != null ? v.toFixed(0) : '—' },
  { key: 'totalYdsPg', label: 'Yds/G',      tip: 'Total yards per game',           fmt: v => v != null ? v.toFixed(0) : '—' },
];

const ADV_COLS = [
  { key: 'off_epa_per_play',       label: 'OFF EPA/Pl',  tip: 'Offensive EPA per play',                                           fmt: v => v?.toFixed(3) ?? '—' },
  { key: 'def_epa_per_play',       label: 'DEF EPA/Pl',  tip: 'Defensive EPA allowed per play (lower = better)',                  fmt: v => v?.toFixed(3) ?? '—', lowerIsBetter: true },
  { key: 'off_success_rate',       label: 'OFF Succ%',   tip: 'Offensive success rate',                                           fmt: v => v != null ? `${v}%` : '—', premium: true },
  { key: 'def_success_rate',       label: 'DEF Succ%',   tip: 'Opponent success rate allowed (lower = better)',                   fmt: v => v != null ? `${v}%` : '—', premium: true, lowerIsBetter: true },
  { key: 'off_explosive_rate',     label: 'OFF Expl%',   tip: 'Offensive explosive play rate (pass ≥20 or run ≥10)',              fmt: v => v != null ? `${v}%` : '—', premium: true },
  { key: 'off_pass_epa_per_att',   label: 'Pass EPA/A',  tip: 'EPA per pass attempt (offense)',                                   fmt: v => v?.toFixed(3) ?? '—', premium: true },
  { key: 'off_rush_epa_per_carry', label: 'Rush EPA/C',  tip: 'EPA per rush carry (offense)',                                     fmt: v => v?.toFixed(3) ?? '—', premium: true },
  { key: 'cpoe',                   label: 'CPOE',        tip: 'Completion % Over Expected',                                       fmt: v => v != null ? `${v > 0 ? '+' : ''}${v}%` : '—', premium: true },
  { key: 'rz_td_rate',             label: 'RZ TD%',      tip: 'Red Zone TD rate',                                                 fmt: v => v != null ? `${v}%` : '—', premium: true },
  { key: 'third_conv_rate',        label: '3rd Conv%',   tip: '3rd Down conversion rate',                                         fmt: v => v != null ? `${v}%` : '—', premium: true },
  { key: 'pressure_rate',          label: 'Press%',      tip: 'QB pressure rate (defense)',                                       fmt: v => v != null ? `${v}%` : '—', premium: true },
  { key: 'turnover_diff',          label: 'TO Diff',     tip: 'Turnover differential',                                            fmt: v => v != null ? `${v > 0 ? '+' : ''}${v}` : '—', premium: true },
];

// ── NFL division map ─────────────────────────────────────────────────────────
const NFL_DIV_MAP = {
  2:'AFC East',15:'AFC East',17:'AFC East',20:'AFC East',
  33:'AFC North',4:'AFC North',5:'AFC North',23:'AFC North',
  34:'AFC South',11:'AFC South',30:'AFC South',10:'AFC South',
  7:'AFC West',12:'AFC West',13:'AFC West',24:'AFC West',
  6:'NFC East',19:'NFC East',21:'NFC East',28:'NFC East',
  3:'NFC North',8:'NFC North',9:'NFC North',16:'NFC North',
  1:'NFC South',29:'NFC South',18:'NFC South',27:'NFC South',
  22:'NFC West',14:'NFC West',26:'NFC West',25:'NFC West',
};

const ESPN_TO_NFL = { WSH: 'WAS', JAX: 'JAC' };

let _basicCache = null;
let _advCache   = null;

// ── Main Component ────────────────────────────────────────────────────────────
export default function TeamStatsTable() {
  const { isPremium, isAdmin, setUpgradeModalOpen } = useAuth();
  const canSeeAdvanced = isPremium || isAdmin;

  const [rows, setRows]           = useState([]);
  const [orderBy, setOrderBy]     = useState('wins');
  const [order, setOrder]         = useState('desc');
  const [search, setSearch]       = useState('');
  const [loading, setLoading]     = useState(true);
  const [advLoading, setAdvLoading] = useState(true);

  // Sync scroll between top dummy scrollbar and main table
  const tableScrollRef = useRef(null);
  const topScrollRef   = useRef(null);
  const onTableScroll  = () => { if (topScrollRef.current && tableScrollRef.current) topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft; };
  const onTopScroll    = () => { if (tableScrollRef.current && topScrollRef.current) tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft; };

  useEffect(() => {
    // Load basic (ESPN standings) + advanced (local JSON) in parallel
    const basicPromise = _basicCache
      ? Promise.resolve(_basicCache)
      : axios.get('https://site.api.espn.com/apis/v2/sports/football/nfl/standings', { timeout: 10000 })
          .then(r => {
            const built = [];
            for (const conf of (r.data?.children || [])) {
              for (const entry of (conf?.standings?.entries || [])) {
                const t = entry.team;
                const sm = Object.fromEntries((entry.stats || []).map(s => [s.name, s.value]));
                const gp = sm.gamesPlayed || (sm.wins + sm.losses + (sm.ties || 0)) || 1;
                const pf = sm.pointsFor ?? null;
                const pa = sm.pointsAgainst ?? null;
                const passYds = sm.passingYardsPerGame ?? (sm.passingYards != null ? sm.passingYards / gp : null);
                const rushYds = sm.rushingYardsPerGame ?? (sm.rushingYards != null ? sm.rushingYards / gp : null);
                built.push({
                  id:         Number(t.id),
                  abbr:       t.abbreviation,
                  nflAbbr:    ESPN_TO_NFL[t.abbreviation] || t.abbreviation,
                  name:       t.shortDisplayName || t.displayName,
                  logo:       t.logos?.[0]?.href,
                  div:        NFL_DIV_MAP[Number(t.id)] || '',
                  wins:       sm.wins ?? 0,
                  losses:     sm.losses ?? 0,
                  ties:       sm.ties ?? 0,
                  pf,  pa,
                  diff:       pf != null && pa != null ? pf - pa : null,
                  pfg:        pf != null ? pf / gp : null,
                  pag:        pa != null ? pa / gp : null,
                  passYdsPg:  typeof passYds === 'number' ? passYds : null,
                  rushYdsPg:  typeof rushYds === 'number' ? rushYds : null,
                  totalYdsPg: typeof passYds === 'number' && typeof rushYds === 'number' ? passYds + rushYds : null,
                });
              }
            }
            built.sort((a, b) => b.wins - a.wins || a.losses - b.losses);
            _basicCache = built;
            return built;
          });

    const advPromise = _advCache
      ? Promise.resolve(_advCache)
      : axios.get('/data/team_advanced_2024.json').then(r => { _advCache = r.data; return r.data; });

    basicPromise.then(basic => {
      setRows(basic);
      setLoading(false);
      advPromise.then(adv => {
        const advMap = adv.teams || {};
        setRows(prev => prev.map(r => {
          const advData = advMap[r.nflAbbr] || advMap[r.abbr] || {};
          return { ...r, ...advData };
        }));
        setAdvLoading(false);
      }).catch(() => setAdvLoading(false));
    }).catch(() => { setLoading(false); setAdvLoading(false); });
  }, []);

  const allCols = [...BASIC_COLS, ...ADV_COLS];

  const handleSort = (key) => {
    const col = allCols.find(c => c.key === key);
    if (col?.premium && !canSeeAdvanced) return;
    const newOrder = orderBy === key && order === 'desc' ? 'asc' : 'desc';
    setOrder(newOrder);
    setOrderBy(key);
    setRows(prev => [...prev].sort((a, b) => {
      const av = a[key] ?? (newOrder === 'desc' ? -Infinity : Infinity);
      const bv = b[key] ?? (newOrder === 'desc' ? -Infinity : Infinity);
      return newOrder === 'desc' ? bv - av : av - bv;
    }));
  };

  const filtered = search
    ? rows.filter(r => r.abbr?.toLowerCase().includes(search.toLowerCase()) || r.name?.toLowerCase().includes(search.toLowerCase()))
    : rows;

  const lockedCount = ADV_COLS.filter(c => c.premium).length;

  return (
    <Box>
      {advLoading && !loading && <LinearProgress color="primary" sx={{ height: 2 }} />}

      {/* ── Search / status bar ───────────────────────────────────────── */}
      <Box sx={{ px: 2, py: 1, borderBottom: '1px solid rgba(245,166,35,0.35)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', backgroundColor: '#131318' }}>
        <TextField
          size="small"
          placeholder="Search team…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">🔍</InputAdornment> }}
          sx={{
            width: 220,
            '& .MuiOutlinedInput-root': { backgroundColor: '#0e0e12' },
            '& .MuiOutlinedInput-root fieldset': { borderColor: 'rgba(245,166,35,0.4)' },
            '& .MuiOutlinedInput-root:hover fieldset': { borderColor: '#f5a623' },
          }}
        />
        <Chip label="ESPN · 2024 Season" size="small" color="success" sx={{ fontSize: '0.6rem', height: 18 }} />
        {!advLoading && <Chip label="⚡ nflverse 2024" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#1c1c24', color: '#2ea84b' }} />}
        <Typography variant="caption" sx={{ color: '#94a3b8', ml: 'auto' }}>
          {filtered.length} teams · click column to sort
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress color="primary" />
        </Box>
      ) : (
        <>
          {/* ── Top dummy scrollbar ──────────────────────────────────── */}
          <Box sx={{ display: 'flex' }}>
            <Box sx={{ flexShrink: 0, width: FROZEN_W }} />
            <Box ref={topScrollRef} onScroll={onTopScroll}
              sx={{ flex: 1, overflowX: 'scroll', overflowY: 'hidden', height: 10, ...SCROLLBAR_SX }}>
              <div style={{ width: (BASIC_COLS.length + ADV_COLS.length) * 90, height: 1 }} />
            </Box>
          </Box>

          {/* ── Main table ───────────────────────────────────────────── */}
          <Box sx={{ display: 'flex', position: 'relative' }}>
            {/* Frozen left: rank + team */}
            <Box sx={{ flexShrink: 0, width: FROZEN_W, zIndex: 3, boxShadow: '4px 0 12px rgba(0,0,0,0.5)' }}>
              <Table size="small" sx={{ tableLayout: 'fixed', width: FROZEN_W }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH, width: 36 }}>#</TableCell>
                    <TableCell sx={{ ...TH }}>Team</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((row, i) => (
                    <TableRow key={row.id} sx={{ height: ROW_H, backgroundColor: '#131318' }}>
                      <TableCell sx={{ color: '#f5a623', fontWeight: 700, fontSize: '0.78rem', py: 0, width: 36 }}>{i + 1}</TableCell>
                      <TableCell sx={{ py: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {row.logo && (
                            <Box component="img" src={row.logo} alt=""
                              sx={{ width: 22, height: 22, objectFit: 'contain', flexShrink: 0 }}
                              onError={e => { e.target.style.display = 'none'; }}
                            />
                          )}
                          <Box>
                            <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#ffffff', lineHeight: 1.1 }}>{row.name}</Typography>
                            <Typography sx={{ fontSize: '0.6rem', color: '#64748b' }}>{row.abbr} · {(row.div || '').replace(/AFC |NFC /, '')}</Typography>
                          </Box>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {/* Scrollable stats columns */}
            <Box ref={tableScrollRef} onScroll={onTableScroll}
              sx={{ flex: 1, overflowX: 'auto', overflowY: 'hidden', maxHeight: ROW_H * (filtered.length + 1) + 10, ...SCROLLBAR_SX }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', minWidth: (BASIC_COLS.length + ADV_COLS.length) * 90 }}>
                <TableHead>
                  <TableRow>
                    {/* Basic cols */}
                    {BASIC_COLS.map(col => (
                      <TableCell key={col.key} align="right" sx={{ ...TH, width: 90 }}>
                        <Tooltip title={col.tip} placement="top" arrow>
                          <TableSortLabel
                            active={orderBy === col.key}
                            direction={orderBy === col.key ? order : 'desc'}
                            onClick={() => handleSort(col.key)}
                            sx={{ '& .MuiTableSortLabel-icon': { color: '#2ea84b !important' }, color: 'inherit' }}>
                            {col.label}
                          </TableSortLabel>
                        </Tooltip>
                      </TableCell>
                    ))}
                    {/* Divider header */}
                    <TableCell sx={{ ...TH, width: 10, px: 0.5, borderLeft: '2px solid #f5a62344' }} />
                    {/* Advanced cols */}
                    {ADV_COLS.map(col => {
                      const locked = col.premium && !canSeeAdvanced;
                      return (
                        <TableCell key={col.key} align="right"
                          sx={{ ...TH, width: 90, color: locked ? '#f5a62355' : TH.color, borderLeft: col.key === ADV_COLS[0].key ? '2px solid #f5a62344' : undefined }}>
                          <Tooltip title={locked ? '⚡ Nerds+ — unlock advanced metrics' : col.tip} placement="top" arrow>
                            <TableSortLabel
                              active={!locked && orderBy === col.key}
                              direction={orderBy === col.key ? order : 'desc'}
                              onClick={() => handleSort(col.key)}
                              sx={{ '& .MuiTableSortLabel-icon': { color: '#2ea84b !important' }, color: 'inherit', cursor: locked ? 'default' : 'pointer' }}>
                              {locked ? `🔒 ${col.label}` : col.label}
                            </TableSortLabel>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((row) => (
                    <TableRow key={row.id} sx={{ height: ROW_H, '&:hover td': { backgroundColor: '#1c1c24' }, cursor: 'default' }}>
                      {/* Basic cols */}
                      {BASIC_COLS.map(col => {
                        const val = row[col.key];
                        const display = col.fmt(val);
                        const isDiff = col.diffCol;
                        const diffColor = isDiff && val != null ? (val > 0 ? '#2ea84b' : val < 0 ? '#ef4444' : '#e8f0f8') : '#e8f0f8';
                        return (
                          <TableCell key={col.key} align="right"
                            sx={{ py: 0, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600, backgroundColor: '#0e0e12',
                              color: val != null ? diffColor : '#334155' }}>
                            {display}
                          </TableCell>
                        );
                      })}
                      {/* Divider cell */}
                      <TableCell sx={{ py: 0, px: 0.5, backgroundColor: '#0e0e12', borderLeft: '2px solid rgba(245,166,35,0.15)' }} />
                      {/* Advanced cols */}
                      {ADV_COLS.map(col => {
                        const locked = col.premium && !canSeeAdvanced;
                        const val = row[col.key];
                        const display = locked ? '—' : (val != null ? col.fmt(val) : (advLoading ? '…' : '—'));
                        return (
                          <TableCell key={col.key} align="right"
                            sx={{
                              py: 0, fontSize: '0.82rem', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                              backgroundColor: '#0e0e12',
                              color: locked ? 'rgba(245,166,35,0.15)' : (val != null ? '#e8f0f8' : '#334155'),
                              filter: locked ? 'blur(4px)' : 'none',
                              userSelect: locked ? 'none' : 'auto',
                              borderLeft: col.key === ADV_COLS[0].key ? '2px solid #f5a62322' : undefined,
                            }}>
                            {display}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>

          {/* ── Upgrade nudge ────────────────────────────────────────── */}
          {!canSeeAdvanced && (
            <Box sx={{
              mt: 1.5, px: 2, py: 1.25,
              display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
              backgroundColor: 'rgba(245,166,35,0.06)', borderRadius: 1.5,
              border: '1px solid #f5a62322',
            }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8', flex: 1 }}>
                🔒 <strong style={{ color: '#f5a623' }}>{lockedCount} advanced columns locked</strong> — Success Rate, Explosive%, Pass/Rush EPA, CPOE, RZ TD%, 3rd Down Conv%, Pressure Rate, TO Diff
              </Typography>
              <Button size="small" variant="outlined"
                onClick={() => setUpgradeModalOpen(true)}
                sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem',
                  borderColor: '#f5a62366', color: '#f5a623', flexShrink: 0,
                  '&:hover': { borderColor: '#f5a623', backgroundColor: 'rgba(245,166,35,0.1)' } }}>
                ⚡ Unlock with Nerds+
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
